import {BLEND_MODES, Bounds, Container, DisplayObject, filters, ObservablePoint, Point, Rectangle, SCALE_MODES, Texture, DEG_TO_RAD} from 'pixi.js';
import {ISlot, IAnimation as ISpineAnimation} from 'pixi-spine';
import {
    DeviceQuality, IAnimation, IService, Map, SystemComponentType
} from '@gongxigames/framework/core/types';
import {
    CoreDI
} from '@gongxigames/framework/core/configs';
import {inject, injectable} from '@gongxigames/framework/core/installer';
import {
    CachedSprite,
    CachedTexture,
    MovieClip, RichTextOptions,
    Spine,
    SpineAnimation, Animation,
} from '@gongxigames/framework/core/fragments/display';
import {MovieClipTween} from '@gongxigames/framework/core/fragments/tween/MovieClipTween';
import {consoleLogger} from '@gongxigames/framework/core/fragments/ConsoleLogger';
import {System} from '@gongxigames/framework/core/services/system/System';
import {ScreenManager} from '@gongxigames/framework/core/services/screenManager/ScreenManager';
import {ObjectBuilder} from '@gongxigames/framework/core/services/ObjectBuilder';
import {Device} from '@gongxigames/framework/core/services/Device';
import {LanguageService} from '@gongxigames/framework/core/services/LanguageService';
import {UpdateManager} from '@gongxigames/framework/core';

@injectable()
export class SpineUtils implements IService {
    public static NAME: symbol = CoreDI.SpineUtils;
    public type: SystemComponentType = 'service';
    public static readonly LOW_DEVICE_RESOLUTION: number = 0.8;

    protected _spineAnimationCache: Map<Map<CachedTexture[]>> = {};

    @inject(CoreDI.System)
    protected _system!: System;

    @inject(CoreDI.ScreenManager)
    protected _screenManager!: ScreenManager;

    @inject(CoreDI.ObjectBuilder)
    protected _objectBuilder!: ObjectBuilder;

    @inject(CoreDI.Device)
    protected _device!: Device;

    @inject(CoreDI.LanguageService)
    private _languageService!: LanguageService;

    @inject(CoreDI.UpdateManager)
    private readonly _updateManager!: UpdateManager;

    public generateFrames(spineName: string, animationName: string, fps = 20): CachedTexture[] {
        if (!this._spineAnimationCache[spineName]) {
            this._spineAnimationCache[spineName] = {};
        }

        if (this._spineAnimationCache[spineName] && this._spineAnimationCache[spineName][animationName]) {
            return this._spineAnimationCache[spineName][animationName];
        }

        const container = new Container();
        const spine = this._objectBuilder.buildSpine(spineName);
        const frames: CachedTexture[] = [];
        const resolution = this._screenManager.defaultViewportResolution;
        const animationNameByQuality = this.getAnimationNameByQuality(spine, animationName);
        const trackEntry = spine.state.setAnimation(0, animationNameByQuality, false);
        const totalTime = trackEntry.animation.duration * 1000;
        let textureResolution = resolution;

        if (this._device.quality <= DeviceQuality.LOW) {
            textureResolution *= SpineUtils.LOW_DEVICE_RESOLUTION;
        }

        container.addChild(spine);
        this.applyMasks(spine);

        spine.start();

        const bounds = new Bounds();
        let time = 0;

        while (time < totalTime) {
            time += 1000 / fps;
            spine.update(1000);

            const region = spine.getLocalBounds();

            bounds.addPoint(region);
            bounds.addPoint({
                x: region.x + region.width,
                y: region.y + region.height
            });
        }

        const region = new Rectangle();
        let frame = 0;

        time = 0;
        bounds.getRectangle(region);

        { // round pixels
            region.x = Math.floor(region.x);
            region.y = Math.ceil(region.y);
            region.width = Math.ceil(region.width);
            region.height = Math.ceil(region.height);
        }

        const position = new Point(region.x, region.y);

        { // move spine to render frame
            spine.x = -region.x;
            spine.y = -region.y;
            region.x = 0;
            region.y = 0;
        }

        while (time < totalTime) {
            time += 1000 / fps;

            const currentTime = time;
            const cachedTexture: CachedTexture = {
                getTexture: () => {
                    if (cachedTexture.texture) {
                        return cachedTexture.texture;
                    }

                    spine.state.clearTracks();
                    spine.skeleton.setToSetupPose();
                    spine.state.setAnimation(0, animationNameByQuality, false);
                    spine.update(currentTime);

                    cachedTexture.position = position;
                    cachedTexture.texture = this._system.renderer.generateTexture(container,{
                        scaleMode: SCALE_MODES.NEAREST,
                        resolution: textureResolution,
                        region
                    });

                    return cachedTexture.texture;
                }
            };

            frames.push(cachedTexture);
            // eslint-disable-next-line unused-imports/no-unused-vars
            frame++;
        }

        this._spineAnimationCache[spineName][animationName] = frames;

        return [...frames];
    }

    public createCachedAnimation(spineName: string, animationName: string, loop = false, fps = 20): IAnimation {
        if (this._device.quality <= DeviceQuality.LOW) {
            fps = 10;
        }

        const frames = this.generateFrames(spineName, animationName, fps);
        const spriteFrames = frames.map(frame => new CachedSprite(frame));
        const mc = new MovieClip(spriteFrames);
        const mcTween = new MovieClipTween(mc, fps, loop);
        const animation = new Animation();

        animation.container.addChild(mc);
        animation.addTween(mcTween);

        return animation;
    }

    public createSpineAnimation(spineName: string, animationName: string, loop?: boolean, skin?: string): IAnimation {
        const spine = this._objectBuilder.buildSpine(spineName);
        const animation = new SpineAnimation(spine, this.getAnimationNameByQuality(spine, animationName), loop, skin);

        this.applyMasks(animation.container as Spine);

        return animation;
    }

    public getAnimationNameByQuality(spine: Spine, name: string): string {
        if (this._device.isSuperLowQuality) {
            if (spine.state.hasAnimation(`${name}[quality_dno]`)) {
                return `${name}[quality_dno]`;
            } else if (spine.state.hasAnimation(`${name}[quality_low]`)) {
                return `${name}[quality_low]`;
            } else if (spine.state.hasAnimation(`${name}[quality_medium]`)) {
                return `${name}[quality_medium]`;
            }
        } else if (this._device.quality === DeviceQuality.LOW) {
            if (spine.state.hasAnimation(`${name}[quality_low]`)) {
                return `${name}[quality_low]`;
            } else if (spine.state.hasAnimation(`${name}[quality_medium]`)) {
                return `${name}[quality_medium]`;
            }
        } else if (this._device.quality === DeviceQuality.MEDIUM) {
            if (spine.state.hasAnimation(`${name}[quality_medium]`)) {
                return `${name}[quality_medium]`;
            }
        }

        return name;
    }

    public createAnimation(spineName: string, animationName: string, loop = false, cached = false,
        fps = 20, skinName = ''): IAnimation {
        if (cached) {
            return this.createCachedAnimation(spineName, animationName, loop, fps);
        } else {
            return this.createSpineAnimation(spineName, animationName, loop, skinName);
        }
    }

    /**
     * This function gets AnimationDuration for animationName of given spine, if animationName not passed it gets
     * current playing animation
     * @param spine
     * @param animationName
     */
    public getAnimationDuration(spine: Spine, animationName?: string): number {
        let animation: ISpineAnimation | null;

        if (!animationName) {
            animation = spine.state.getCurrent(0)?.animation;
        } else {
            animation = spine.state.data.skeletonData.findAnimation(this.getAnimationNameByQuality(spine, animationName));
        }

        if (animation) {
            return animation.duration * 1000;
        } else {
            consoleLogger.warn(`${animationName} not found for current spine.`);
        }

        return 0;
    }

    /**
     * This function get boneWorldPosition for slot of given spine including parent position
     * @param spine
     * @param slotName
     */
    public getBoneWorldPosition(spine: Spine, slotName: string): Point {
        const slot = spine.skeleton.findSlot(slotName);

        if (!slot) {
            consoleLogger.warn(`${slotName} not found for current spine.`);

            return new Point(0, 0);
        }

        const x = slot.bone.worldX + spine.x;
        const y = slot.bone.worldY + spine.y;

        return new Point(x, y);
    }

    /**
     * This function set last frame for animation of given spine
     * @param spine
     * @param animationName
     */
    public setAnimationLastFrame(spine: Spine, animationName: string): void {
        const name = this.getAnimationNameByQuality(spine, animationName);
        const animation = spine.state.data.skeletonData.findAnimation(name);

        if (!animation) {
            consoleLogger.warn(`${animationName} not found for current spine.`);

            return;
        }

        spine.state.setAnimation(0, name, false).trackTime = Number.MAX_SAFE_INTEGER;
        spine.start().update(9999);
    }

    public applyMasks(spine: Spine): void {
        for (const slot of spine.skeleton.slots) {
            if (slot.currentMeshName) {
                this.applyMaskToMesh(slot, spine);
            } else if (slot.currentSpriteName) {
                this.applyMaskToSprite(slot, spine);
            }
        }
    }

    protected applyMaskToMesh(slot: ISlot, spine: Spine): void {
        const maskIndex = slot.data.name.indexOf('[mask_');

        if (maskIndex !== -1) {
            const maskName = slot.data.name.slice(maskIndex + 1, slot.data.name.indexOf(']', maskIndex));
            const maskSlot = spine.skeleton.findSlot(maskName);

            if (!maskSlot) {
                consoleLogger.warn(`There is no mask "${maskName}"`);
            } else {
                slot.currentMesh.mask = maskSlot.currentSprite;
                spine.addToPreventRenderMaskList(maskSlot.currentSprite);

                if (slot.blendMode === BLEND_MODES.ADD) {
                    const addFilter = new filters.AlphaFilter();

                    addFilter.blendMode = BLEND_MODES.ADD;
                    slot.currentMesh.filters = [addFilter];
                    slot.currentMesh.blendMode = BLEND_MODES.NORMAL;
                }
            }
        }
    }

    /**
     * Replaces the current sprite (usually added by technical artist) in a specified Spine slot with a new one.
     *
     * This method removes any existing children from the slot's current sprite,
     * clears its texture, and attaches the provided `displayObject` as the new visual element.
     * Optionally applies a custom scale to the slot; if none is provided,
     * defaults to a scale of (1, -1).
     *
     * @param {Spine} spine - The Spine instance containing the skeleton and slots.
     * @param {DisplayObject} displayObject - The new display object to add to the slot.
     * @param {string} slotName - The name of the Spine slot where the object will be replaced.
     * @param {ObservablePoint|Point} [slotScale] - Optional scale to apply to the slot’s sprite.
     * @returns {DisplayObject} The display object that was added to the slot.
     */
    public addToSlot(spine: Spine, displayObject: DisplayObject, slotName: string, slotScale?: ObservablePoint|Point): DisplayObject {
        const slot = spine.skeleton.findSlot(slotName);
        const {currentSprite} = slot;

        currentSprite.removeChildren();
        currentSprite.texture = Texture.EMPTY;
        currentSprite.addChild(displayObject);

        if (slotScale) {
            slot.currentSprite.scale = slotScale;

        } else {
            slot.currentSprite.scale.set(1, -1);
        }

        return displayObject;
    }

    /**
     * Attaches a given DisplayObject to a Spine slot so that it automatically follows
     * the corresponding bone's transformation on every animation frame.
     *
     * It doesn't remove already added objects to the slot
     *
     * The display object will be positioned and rotated according to the bone
     * attached to the specified slot. Optionally, a custom scale can be applied.
     *
     * Returns a cleanup function that detaches the object and stops the updates.
     *
     * @param spine - The Spine instance to which the display object should be attached.
     * @param displayObject - The display object (e.g. Sprite, Graphics, Container) to attach to the slot.
     * @param slotName - The name of the Spine slot to attach the object to.
     * @param slotScale - (Optional) A custom scale to apply to the object.
     *                    If not provided, the default scale `{x: 1, y: -1}` is used.
     *
     * @returns A cleanup function that detaches the object from the Spine instance and cancels frame updates:
     * ```ts
     * const detach = attachToSlot(spine, displayObject, 'sym2_spine');
     * ...
     * detach(); // stops updates and removes the display object from Spine
     * ```
     *
     * @example
     * // Example: attach a sword sprite to the character’s right hand
     * const coin = new PIXI.Sprite(swordTexture);
     * const detachCoin = attachToSlot(spine, coin, 'sym2_spine');
     *
     * // Later, when you want to remove it
     * detachCoin();
     */
    public attachToSlot(spine: Spine, displayObject: DisplayObject, slotName: string, slotScale?: ObservablePoint | Point): () => void {
        const slot = spine.skeleton.findSlot(slotName);
        const {bone} = slot;

        spine.addChild(displayObject);

        const updateAttachment = () => {
            if (displayObject.parent) {
                displayObject.position.set(bone.worldX, bone.worldY);
                displayObject.rotation = bone.rotation * DEG_TO_RAD;

                if (slotScale) {
                    displayObject.scale = slotScale;

                } else {
                    displayObject.scale.set(1, -1);
                }
            }

        };

        this._updateManager.addUpdateFunction(updateAttachment);

        return () => {
            spine.removeChild(displayObject);
            this._updateManager.removeUpdateFunction(updateAttachment);
        };
    }

    /**
     * This function add mixed possibility to localise slot in spine
     * 1) if there is a translation, it will create a RichText
     * 2) else if there is a custom display object in options parameter, it will add it
     * 3) else it will leave a default texture, defined by an animator
     * @param spine
     * @param options
     * @param slotName
     */
    public addLocalisationToSlot(spine: Spine, options: SpineLocalisationOptions, slotName: string ): void {
        if (this._languageService.get(options.textOptions.id) !== 'undefined') {
            const text = this._objectBuilder.buildRichText(options.textOptions);

            this.addToSlot(spine, text, slotName);
        } else if (options.displayObject) {
            this.addToSlot(spine, options.displayObject, slotName);
        }
    }

    protected applyMaskToSprite(slot: ISlot, spine: Spine): void {
        const maskIndex = slot.data.name.indexOf('[mask_');

        if (maskIndex !== -1) {
            const maskName = slot.data.name.slice(maskIndex + 1, slot.data.name.indexOf(']', maskIndex));
            const maskSlot = spine.skeleton.findSlot(maskName);

            if (!maskSlot) {
                consoleLogger.warn(`There is no mask "${maskName}"`);
            } else {
                slot.currentSprite.mask = maskSlot.currentSprite;
                spine.addToPreventRenderMaskList(maskSlot.currentSprite);

                if (slot.blendMode === BLEND_MODES.ADD) {
                    const addFilter = new filters.AlphaFilter();

                    addFilter.blendMode = BLEND_MODES.ADD;
                    slot.currentSprite.filters = [addFilter];
                    slot.currentSprite.blendMode = BLEND_MODES.NORMAL;
                }
            }
        }
    }
}

export type SpineLocalisationOptions = {
    textOptions: RichTextOptions;
    displayObject?: DisplayObject;
};
